import React from 'react';
import type { Settings } from 'react-slick';
import type { UseQueryResult } from '@tanstack/react-query';
import { useWindowSize } from 'usehooks-ts';

import {
	EmptyView,
	LoadingView,
	QueryViewStateHandler,
	SuccessView,
} from 'Components/QueryViewStateHandler/QueryViewStateHandler';
import type { GetSimilarVehiclesResponse } from 'Services/api/vehicles.types';

import { SimilarVehiclesCard } from './SimilarVehiclesCard/SimilarVehiclesCard';
import { SimilarVehiclesCarousel } from './SimilarVehiclesCarousel/SimilarVehiclesCarousel';
import { SimilarVehiclesEmptyState } from './SimilarVehiclesEmptyState/SimilarVehiclesEmptyState';
import { SimilarVehiclesWrapper } from './SimilarVehiclesWrapper/SimilarVehiclesWrapper';

export type SimilarVehiclesSliderSettings = Omit<Settings, 'centerMode'> & {
	hideDefaultNextArrow?: boolean;
	hideDefaultPrevArrow?: boolean;
	showOverflow?: boolean;
};

export interface SimilarVehiclesEvents {
	onError?: () => void;
	onNextArrowClick?: () => void;
	onPrevArrowClick?: () => void;
	onShortlisted?: (enquiryId: number, url: string, isChecked?: boolean) => void;
	onSimilarVehiclesVisible?: () => void;
	onVehicleCardClick?: (enquiryId: number, url: string) => void;
	onViewAllVehiclesClick?: (url: string) => void;
}

type LoadingAndSuccessViewProps = {
	customLoadingView?: React.ReactNode;
	events?: SimilarVehiclesEvents;
	maxImageWidth?: number;
	queryState: UseQueryResult<GetSimilarVehiclesResponse>;
	sliderSettings?: SimilarVehiclesSliderSettings;
	vehicleCardClassName?: string;
};

interface SimilarVehiclesProps extends LoadingAndSuccessViewProps {
	emptyStateClassName?: string;
	emptyStateDescription?: React.ReactNode;
	emptyStateTitle?: React.ReactNode;
	events?: SimilarVehiclesEvents;
}

const LoadingAndSuccessView: React.FC<LoadingAndSuccessViewProps> = ({
	customLoadingView,
	events,
	maxImageWidth,
	queryState: { data, isLoading },
	sliderSettings,
	vehicleCardClassName,
}) => {
	const { width = 0 } = useWindowSize();
	const { onNextArrowClick, onPrevArrowClick, onShortlisted, onVehicleCardClick } = events || {};

	return (
		<SimilarVehiclesCarousel
			/* Reinitialise the carousel when the window width changes to avoid layout issues */
			key={width}
			customLoadingView={customLoadingView}
			isLoading={isLoading}
			onNextArrowClick={onNextArrowClick}
			onPrevArrowClick={onPrevArrowClick}
			sliderSettings={sliderSettings}
			vehicleCardClassName={vehicleCardClassName}
		>
			{data?.similarVehicles?.map((vehicle) => {
				return (
					<SimilarVehiclesCard
						key={vehicle.id}
						maxImageWidth={maxImageWidth}
						onShortlisted={onShortlisted}
						onVehicleCardClick={onVehicleCardClick}
						vehicle={vehicle}
					/>
				);
			})}
		</SimilarVehiclesCarousel>
	);
};

export const SimilarVehicles: React.FC<SimilarVehiclesProps> = ({
	customLoadingView,
	emptyStateClassName,
	emptyStateDescription,
	emptyStateTitle,
	events = {},
	maxImageWidth,
	queryState,
	sliderSettings,
	vehicleCardClassName,
}) => {
	const { data, isError, isLoading, isSuccess } = queryState;
	const isMissingData = !data || !data.similarVehicles;

	const { onError, onSimilarVehiclesVisible } = events;

	if (!isLoading && (isError || isMissingData)) {
		// When there is an error or no data or similarVehicles is null we hide the section
		onError?.();
	}

	return (
		<SimilarVehiclesWrapper onSimilarVehiclesVisible={onSimilarVehiclesVisible}>
			<QueryViewStateHandler
				isEmpty={isSuccess && data.similarVehicles?.length === 0}
				isError={isError}
				isLoading={isLoading}
				isSuccess={isSuccess}
			>
				<LoadingView>
					<LoadingAndSuccessView
						customLoadingView={customLoadingView}
						events={events}
						maxImageWidth={maxImageWidth}
						queryState={queryState}
						sliderSettings={sliderSettings}
						vehicleCardClassName={vehicleCardClassName}
					/>
				</LoadingView>
				<EmptyView>
					<SimilarVehiclesEmptyState
						className={emptyStateClassName}
						description={emptyStateDescription}
						title={emptyStateTitle}
					/>
				</EmptyView>
				<SuccessView>
					<LoadingAndSuccessView
						customLoadingView={customLoadingView}
						events={events}
						maxImageWidth={maxImageWidth}
						queryState={queryState}
						sliderSettings={sliderSettings}
						vehicleCardClassName={vehicleCardClassName}
					/>
				</SuccessView>
			</QueryViewStateHandler>
		</SimilarVehiclesWrapper>
	);
};
